import { useEffect, useRef, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Mic, Square, PhoneOff, MessagesSquare, Send, Loader2, CheckCircle2, ArrowLeft,
  Volume2, VolumeX, RefreshCw, HelpCircle, Lightbulb, GraduationCap, RotateCcw, SkipForward,
  User, UserRound, Gauge, FileText, ChevronDown, ChevronUp, Scale, TrendingUp, Repeat,
} from "lucide-react";

const AVATARS = [
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=faces&fit=crop&w=400&h=400&q=80",
  "https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?crop=faces&fit=crop&w=400&h=400&q=80",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=faces&fit=crop&w=400&h=400&q=80",
  "https://images.pexels.com/photos/38197025/pexels-photo-38197025.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
];

const SUB_LABELS = [
  ["communication", "Communication"], ["technical_accuracy", "Technical"],
  ["business_understanding", "Business"], ["investigation_methodology", "Methodology"],
  ["tool_knowledge", "Tools"],
];

const PERSONA_OPTS = [
  { id: "hr", label: "HR Recruiter" },
  { id: "hiring_manager", label: "Hiring Manager" },
  { id: "technical_lead", label: "Technical Lead" },
  { id: "director", label: "Director / Exec" },
];
const DIFF_OPTS = [
  { id: "beginner", label: "Beginner" }, { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" }, { id: "senior", label: "Senior" }, { id: "pressure", label: "Pressure" },
];
const REV_LEVELS = [
  { id: "strong", label: "Strong" }, { id: "average", label: "Average" }, { id: "weak", label: "Weak" },
  { id: "junior", label: "Junior" }, { id: "senior", label: "Senior" }, { id: "poor_comm", label: "Poor communicator" },
];

function voiceFor(persona, gender) {
  if (gender === "female") {
    return { hr: "shimmer", director: "sage", technical_lead: "coral", hiring_manager: "nova" }[persona] || "nova";
  }
  return { director: "echo", hr: "onyx", technical_lead: "onyx", hiring_manager: "echo" }[persona] || "onyx";
}

// In-conversation voice/text commands. Matched only on short messages so real answers aren't misread.
const COMMANDS = [
  { action: "answer", re: /\b(answer (this|it|the question)( for me)?|what should i say|show me (the|a) (best|good|model|sample) answer|coach me|give me (the|a|your) (best|model|sample|senior|star) answer|help me answer)\b/i },
  { action: "explain", re: /\b(explain (the|this)|what (are|do) you (mean|asking)|what('s| is) (the|this) question|clarify|simplify|rephrase)\b/i },
  { action: "hint", re: /\b(hint|give me a (hint|clue)|point me|nudge)\b/i },
  { action: "retry", re: /\b(try again|let me try( again)?|ask me (that|the question)( again)?|retry|i('?ll| will) try)\b/i },
  { action: "repeat", re: /\b(repeat( that| the question)?|say that again|come again|one more time)\b/i },
  { action: "skip", re: /\b(skip( this| that| the question)?|move on|next question|pass on this)\b/i },
];
const YES_RE = /\b(yes|yeah|yep|sure|ok|okay|let'?s|i'?ll try|ready|go ahead|please do)\b/i;

function detectCommand(text) {
  const t = (text || "").trim();
  if (!t || t.length > 70) return null;
  for (const c of COMMANDS) if (c.re.test(t)) return c.action;
  return null;
}

function fmt(s) {
  const m = Math.floor(s / 60), r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function Block({ title, items, tone }) {
  const c = { emerald: "text-emerald-500", amber: "text-amber-500", red: "text-red-500", slate: "text-muted-foreground" }[tone];
  if (!items || !items.length) return null;
  return (
    <div className="mt-3 text-sm">
      <div className={`font-semibold ${c}`}>{title}</div>
      <ul className="mt-0.5 space-y-0.5">{items.map((it, i) => <li key={i} className="text-muted-foreground flex gap-1.5"><span className={c}>•</span>{it}</li>)}</ul>
    </div>
  );
}

export default function LiveInterview({ apiBase, accent, modes, onGraded, testidPrefix }) {
  const [phase, setPhase] = useState("select");
  const [mode, setMode] = useState(null);
  const [label, setLabel] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  const [closing, setClosing] = useState(false);
  const [answered, setAnswered] = useState(0);
  const [target, setTarget] = useState(7);
  const [seconds, setSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [muted, setMuted] = useState(false);
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [report, setReport] = useState(null);
  const [coachMode, setCoachMode] = useState(false);

  // Setup options
  const [persona, setPersona] = useState("hiring_manager");
  const [gender, setGender] = useState("female");
  const [difficulty, setDifficulty] = useState("intermediate");
  const [jd, setJd] = useState("");
  const [showJd, setShowJd] = useState(false);
  const [jdSaving, setJdSaving] = useState(false);
  const [reversal, setReversal] = useState(false);
  const [revLevel, setRevLevel] = useState("average");
  const [reversalMode, setReversalMode] = useState(false);

  useEffect(() => {
    api.get("/interview-prep/jd", { params: { lab: apiBase } }).then(({ data }) => {
      if (data.jd) { setJd(data.jd); setShowJd(true); }
    }).catch(() => {});
  }, [apiBase]);
  const saveJd = async () => {
    setJdSaving(true);
    try { await api.post("/interview-prep/jd", { jd: jd.trim(), lab: apiBase }); toast.success("Saved — we'll remember this for next time."); }
    catch (e) { toast.error(apiErr(e)); } finally { setJdSaving(false); }
  };

  // Compare-my-answer
  const [compare, setCompare] = useState({}); // { [msgIndex]: {loading, data} }
  const attemptsRef = useRef({}); // { [question]: [score, score] }
  const [attemptsVer, setAttemptsVer] = useState(0);

  const endRef = useRef(null);
  const recRef = useRef(null);
  const baseRef = useRef("");
  const mutedRef = useRef(false);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const voiceRef = useRef("nova");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const pendingRetryRef = useRef(false);
  const [handsFree, setHandsFree] = useState(true);
  const handsFreeRef = useRef(true);
  const busyRef = useRef(false);
  const listeningRef = useRef(false);
  const inputRef = useRef("");
  const silenceRef = useRef(null);
  const autoStopRef = useRef(false);
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
  useEffect(() => { busyRef.current = busy; }, [busy]);
  useEffect(() => { listeningRef.current = listening; }, [listening]);
  useEffect(() => { inputRef.current = input; }, [input]);

  // Auto-open the candidate mic ~0.5s after the interviewer finishes speaking (hands-free turn-taking).
  const maybeAutoListen = () => {
    if (!handsFreeRef.current || !sttOk) return;
    setTimeout(() => {
      if (handsFreeRef.current && !busyRef.current && !listeningRef.current) startListen();
    }, 550);
  };
  // Called when VAD detects end-of-answer (silence): stop and auto-submit.
  const finishAuto = () => {
    clearTimeout(silenceRef.current);
    autoStopRef.current = true;
    try { recRef.current?.stop(); } catch (e) { /* noop */ }
    const t = inputRef.current.trim();
    if (!t || busyRef.current) return;
    setTimeout(() => submitOrCommand(t), 150);
  };
  const ttsOk = typeof window !== "undefined" && "speechSynthesis" in window;
  const sttOk = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const recorderOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window !== "undefined" && "MediaRecorder" in window;
  const micOk = sttOk || recorderOk;

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => () => { stopAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (phase === "call") {
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const cancelSpeech = () => {
    try { if (ttsOk) window.speechSynthesis.cancel(); } catch (e) { /* noop */ }
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch (e) { /* noop */ }
    setSpeaking(false);
  };
  const stopAll = () => {
    cancelSpeech();
    clearTimeout(silenceRef.current);
    autoStopRef.current = true;
    try { recRef.current?.stop(); } catch (e) { /* noop */ }
    try { mediaRef.current?.stop(); } catch (e) { /* noop */ }
    setListening(false);
  };

  const browserSpeak = useCallback((text, onEnd) => {
    if (!ttsOk || mutedRef.current) { onEnd?.(); return; }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const all = window.speechSynthesis.getVoices();
      const pref = all.find((v) => /natural|neural|google (uk|us) english|aria|jenny|guy|libby/i.test(v.name) && v.lang.startsWith("en"))
        || all.find((v) => v.lang === "en-US") || all.find((v) => v.lang && v.lang.startsWith("en"));
      if (pref) u.voice = pref;
      u.rate = 1.0; u.pitch = 1.0;
      u.onstart = () => setSpeaking(true);
      u.onend = () => { setSpeaking(false); onEnd?.(); };
      u.onerror = () => { setSpeaking(false); onEnd?.(); };
      window.speechSynthesis.speak(u);
    } catch (e) { onEnd?.(); }
  }, [ttsOk]);

  const speak = useCallback(async (text, onEnd) => {
    if (mutedRef.current || !text) { onEnd?.(); return; }
    try {
      if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* noop */ } audioRef.current = null; }
      if (ttsOk) window.speechSynthesis.cancel();
      const { data } = await api.post("/tts", { text, voice: voiceRef.current }, { responseType: "blob" });
      if (mutedRef.current) { onEnd?.(); return; }
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setSpeaking(true);
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); onEnd?.(); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); browserSpeak(text, onEnd); };
      await audio.play();
    } catch (e) {
      browserSpeak(text, onEnd);
    }
  }, [ttsOk, browserSpeak]);

  const start = async (m) => {
    if (reversal) return startReversal(m);
    setBusy(true); setReport(null); setFinished(false); setClosing(false); setReversalMode(false); setMsgs([]); setSeconds(0);
    setAnswered(0); setCoachMode(false); setCompare({}); attemptsRef.current = {};
    pendingRetryRef.current = false;
    setAvatar(AVATARS[Math.floor(Math.random() * AVATARS.length)]);
    voiceRef.current = voiceFor(persona, gender);
    try {
      const { data } = await api.post(`/${apiBase}/interview/start`, { mode: m.id, persona, difficulty, jd: jd.trim() });
      setMode(m.id); setLabel(data.label); setTarget(data.questions_target);
      setMsgs([{ role: "interviewer", text: data.message }]);
      setPhase("call");
      setTimeout(() => speak(data.message, maybeAutoListen), 300);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const startReversal = (m) => {
    setReport(null); setFinished(false); setClosing(false); setReversalMode(true); setSeconds(0);
    setAnswered(0); setCoachMode(false); setCompare({}); pendingRetryRef.current = false;
    setAvatar(AVATARS[Math.floor(Math.random() * AVATARS.length)]);
    voiceRef.current = voiceFor(persona, gender === "female" ? "male" : "female"); // candidate voice differs from you
    setMode(m.id); setLabel(m.label + " · Role Reversal");
    const lvl = REV_LEVELS.find((r) => r.id === revLevel)?.label || "candidate";
    setMsgs([{ role: "coach", text: `Role reversal — you're the interviewer now. Ask this ${lvl} candidate a question (type or use the mic). I'll answer as them, then tell you why the answer is strong or weak.` }]);
    setPhase("call");
  };

  const sendReversal = async (text) => {
    const t = (text ?? input).trim(); if (!t || busy) return;
    stopListen(); cancelSpeech();
    setMsgs((m) => [...m, { role: "candidate", text: t }]); setInput(""); setBusy(true);
    try {
      const history = msgs.filter((x) => x.role !== "coach")
        .map((x) => ({ role: x.role === "candidate" ? "interviewer" : "candidate", text: x.text })).slice(-6);
      const { data } = await api.post(`/${apiBase}/reversal`, { mode, level: revLevel, question: t, history });
      setMsgs((m) => [...m, { role: "aicand", text: data.answer || "(no answer)" }]);
      if (data.note) setMsgs((m) => [...m, { role: "coach", text: "Why this answer: " + data.note }]);
      speak(data.answer, maybeAutoListen);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const lastQuestion = () => {
    for (let i = msgs.length - 1; i >= 0; i--) if (msgs[i].role === "interviewer") return msgs[i].text;
    return "";
  };
  const questionBefore = (idx) => {
    for (let i = idx - 1; i >= 0; i--) if (msgs[i].role === "interviewer") return msgs[i].text;
    return "";
  };

  const submitAnswer = async (text) => {
    const t = (text ?? input).trim(); if (!t || busyRef.current) return;
    stopListen(); cancelSpeech();
    pendingRetryRef.current = false;
    setCoachMode(false);
    setMsgs((m) => [...m, { role: "candidate", text: t }]); setInput(""); setBusy(true);
    // Ride through a brief backend blip (deploy/restart/network) with a few backoff retries so a turn is never silently dropped.
    const post = async () => {
      let lastErr;
      for (const wait of [0, 1200, 2600, 4000]) {
        if (wait) await new Promise((r) => setTimeout(r, wait));
        try { return await api.post(`/${apiBase}/interview/message`, { mode, text: t }, { timeout: 45000 }); }
        catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          if (status && status !== 502 && status !== 503 && status !== 504) break; // real 4xx/5xx (not a transient gateway) — stop retrying
        }
      }
      throw lastErr;
    };
    try {
      const { data } = await post();
      setMsgs((m) => [...m, { role: "interviewer", text: data.message }]);
      setAnswered(data.answered); setFinished(data.finished); setClosing(!!data.closing);
      speak(data.message, data.finished ? undefined : maybeAutoListen);
    } catch (e) {
      // Never leave the candidate stranded: drop the un-answered bubble, restore their words, and reopen the mic.
      setMsgs((m) => { const c = [...m]; if (c.length && c[c.length - 1].role === "candidate") c.pop(); return c; });
      setInput(t);
      toast.error("Couldn't reach the interviewer just now — your answer is back in the box. Try again.");
      maybeAutoListen();
    } finally { setBusy(false); }
  };

  const runCommand = async (action) => {
    if (busy) return;
    stopListen(); cancelSpeech();
    if (action === "repeat" || action === "retry") {
      const q = lastQuestion();
      if (!q) return;
      pendingRetryRef.current = false;
      if (action === "retry") setMsgs((m) => [...m, { role: "coach", text: "No problem — here's the question again. Take your time." }]);
      setCoachMode(false);
      speak(action === "retry" ? `Sure. ${q}` : q, maybeAutoListen);
      return;
    }
    if (action === "skip") {
      await submitAnswer("I'd like to skip this one and move on to the next question, please.");
      return;
    }
    setInput(""); setBusy(true); setCoachMode(true);
    try {
      const q = lastQuestion();
      const { data } = await api.post(`/${apiBase}/interview/coach`, { mode, action, question: q });
      setMsgs((m) => [...m, { role: "coach", text: data.text }]);
      if (action === "answer") {
        const followup = "Would you like to try answering the question yourself now?";
        setMsgs((m) => [...m, { role: "interviewer", text: followup }]);
        pendingRetryRef.current = true;
        setCoachMode(false);
        speak(data.text, () => speak(followup, maybeAutoListen));
      } else {
        speak(data.text, maybeAutoListen);
      }
    } catch (e) { toast.error(apiErr(e)); setCoachMode(false); } finally { setBusy(false); }
  };

  // Compare my answer
  const runCompare = async (idx) => {
    const answer = msgs[idx]?.text; const question = questionBefore(idx);
    if (!answer) return;
    setCompare((c) => ({ ...c, [idx]: { loading: true } }));
    try {
      const { data } = await api.post(`/${apiBase}/interview/compare`, { mode, question, answer });
      const key = question || `q${idx}`;
      const arr = attemptsRef.current[key] || [];
      arr.push(data.score); attemptsRef.current[key] = arr; setAttemptsVer((v) => v + 1);
      setCompare((c) => ({ ...c, [idx]: { loading: false, data, question } }));
    } catch (e) { toast.error(apiErr(e)); setCompare((c) => ({ ...c, [idx]: undefined })); }
  };
  const retryQuestion = (question) => {
    if (!question) return;
    cancelSpeech();
    setMsgs((m) => [...m, { role: "interviewer", text: question }]);
    speak(question);
  };

  // Route an explicit transcript (from typing OR voice) to the right handler. Uses explicit text to avoid stale-state closures.
  const submitOrCommand = async (raw) => {
    const t = (raw || "").trim(); if (!t || busyRef.current) return;
    if (reversalMode) { setInput(""); return sendReversal(t); }
    if (pendingRetryRef.current && t.length <= 40 && YES_RE.test(t) && !detectCommand(t)) {
      setInput(""); return runCommand("retry");
    }
    const cmd = detectCommand(t);
    if (cmd) { setInput(""); return runCommand(cmd); }
    return submitAnswer(t);
  };

  const send = async () => submitOrCommand(input);

  const endCall = async () => {
    if (reversalMode) { backToModes(); return; }
    stopAll(); setBusy(true);
    try {
      const { data } = await api.post(`/${apiBase}/interview/finish`, { mode });
      setReport(data); setPhase("report"); onGraded?.();
      toast[data.passed ? "success" : "message"](data.passed ? "You passed this interview!" : "Interview scored — keep practicing.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const startListen = () => {
    if (sttOk) {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = "en-US"; rec.interimResults = true; rec.continuous = true;
      baseRef.current = input.trim();
      rec.onresult = (e) => {
        let txt = ""; for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
        setInput((baseRef.current ? baseRef.current + " " : "") + txt);
        if (handsFreeRef.current) {
          clearTimeout(silenceRef.current);
          silenceRef.current = setTimeout(() => finishAuto(), 1300); // ~1.3s of silence = end of answer
        }
      };
      rec.onend = () => {
        setListening(false);
        clearTimeout(silenceRef.current);
        if (handsFreeRef.current && !autoStopRef.current && !busyRef.current) {
          const t = inputRef.current.trim();
          if (t) {
            // Chrome ended recognition on its own but we have a transcript — submit it.
            autoStopRef.current = true;
            setTimeout(() => submitOrCommand(t), 150);
          } else {
            // No speech yet — keep the mic open (candidate is still thinking).
            setTimeout(() => { if (handsFreeRef.current && !listeningRef.current && !busyRef.current) startListen(); }, 300);
          }
        }
        autoStopRef.current = false;
      };
      rec.onerror = (ev) => {
        setListening(false);
        clearTimeout(silenceRef.current);
        if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
          autoStopRef.current = true; setHandsFree(false);
          toast.error("Microphone blocked — turn on mic access to use hands-free, or type your answer.");
        }
      };
      recRef.current = rec;
      try {
        if (ttsOk) window.speechSynthesis.cancel();
        if (audioRef.current) { try { audioRef.current.pause(); } catch (er) { /* noop */ } }
        setSpeaking(false);
        autoStopRef.current = false;
        rec.start(); setListening(true);
      } catch (e) { setListening(false); }
      return;
    }
    if (!recorderOk) return;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream);
        chunksRef.current = [];
        mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
        mr.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
          if (!blob.size) return;
          setBusy(true);
          try {
            const fd = new FormData();
            fd.append("file", blob, "answer.webm");
            const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setInput((prev) => (prev ? prev + " " : "") + (data.text || ""));
          } catch (e) { toast.error("Could not transcribe that — you can type your answer."); } finally { setBusy(false); }
        };
        if (ttsOk) window.speechSynthesis.cancel();
        try { audioRef.current?.pause(); } catch (er) { /* noop */ }
        setSpeaking(false);
        mediaRef.current = mr; mr.start(); setListening(true);
      } catch (e) { toast.error("Microphone blocked — you can type your answer."); }
    })();
  };
  const stopListen = () => {
    autoStopRef.current = true;
    clearTimeout(silenceRef.current);
    try { recRef.current?.stop(); } catch (e) { /* noop */ }
    try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch (e) { /* noop */ }
    setListening(false);
  };
  const toggleMic = () => (listening ? stopListen() : startListen());

  const backToModes = () => { stopAll(); setPhase("select"); setMode(null); setReport(null); setCoachMode(false); setReversalMode(false); };

  // -------- Mode selection + setup
  if (phase === "select") {
    const Pill = ({ active, onClick, children, tid }) => (
      <button onClick={onClick} data-testid={tid}
        className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${active ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`}
        style={active ? { background: accent } : {}}>{children}</button>
    );
    return (
      <div className="max-w-4xl mx-auto px-6 py-8" data-testid={`${testidPrefix}-interview`} style={{ ["--acc"]: accent }}>
        <h2 className="text-xl font-bold">Live Mock Interviews</h2>
        <p className="text-sm text-muted-foreground mt-1">Your interviewer <strong>speaks the questions aloud</strong> — answer by voice or type. Ask it to explain, hint, or answer a question for you, ask it about the role, or hit <strong>Compare my answer</strong> to see a model answer. Score 50+ to clear a mode.</p>
        {!micOk && <div className="mt-3 text-xs text-amber-600">Your browser can't capture the microphone — you can type your answers.</div>}

        <div className="mt-6 rounded-xl border border-border bg-card p-5 space-y-4" data-testid={`${testidPrefix}-iv-setup`}>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><User className="h-4 w-4" style={{ color: accent }} /> Interviewer persona</div>
            <div className="flex flex-wrap gap-2">
              {PERSONA_OPTS.map((p) => <Pill key={p.id} active={persona === p.id} onClick={() => setPersona(p.id)} tid={`${testidPrefix}-iv-persona-${p.id}`}>{p.label}</Pill>)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><UserRound className="h-4 w-4" style={{ color: accent }} /> Voice</div>
            <div className="flex flex-wrap gap-2">
              <Pill active={gender === "female"} onClick={() => setGender("female")} tid={`${testidPrefix}-iv-voice-female`}>Female</Pill>
              <Pill active={gender === "male"} onClick={() => setGender("male")} tid={`${testidPrefix}-iv-voice-male`}>Male</Pill>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Gauge className="h-4 w-4" style={{ color: accent }} /> Difficulty</div>
            <div className="flex flex-wrap gap-2">
              {DIFF_OPTS.map((d) => <Pill key={d.id} active={difficulty === d.id} onClick={() => setDifficulty(d.id)} tid={`${testidPrefix}-iv-diff-${d.id}`}>{d.label}</Pill>)}
            </div>
          </div>
          <div>
            <button onClick={() => setShowJd((v) => !v)} data-testid={`${testidPrefix}-iv-jd-toggle`} className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" style={{ color: accent }} /> Tailor to a job description or resume {showJd ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {jd.trim() && <span className="text-[10px] rounded-full px-2 py-0.5 text-white" style={{ background: accent }}>added</span>}
            </button>
            {showJd && (
              <>
                <textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={4} data-testid={`${testidPrefix}-iv-jd-input`}
                  placeholder="Paste a job description or your resume — the interviewer will base its questions on it (skills, tools, responsibilities, claims to probe)."
                  className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none" />
                <button onClick={saveJd} disabled={jdSaving} data-testid={`${testidPrefix}-iv-jd-save`}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-[color:var(--acc)]">
                  {jdSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} Save for next time
                </button>
              </>
            )}
          </div>
          <div className="border-t border-border pt-4">
            <button onClick={() => setReversal((v) => !v)} data-testid={`${testidPrefix}-iv-reversal-toggle`} className="flex items-center gap-2 text-sm font-semibold">
              <Repeat className="h-4 w-4" style={{ color: accent }} /> Role reversal — you interview an AI candidate
              <span className={`ml-1 text-[10px] rounded-full px-2 py-0.5 ${reversal ? "text-white" : "border border-border text-muted-foreground"}`} style={reversal ? { background: accent } : {}}>{reversal ? "ON" : "OFF"}</span>
            </button>
            {reversal && (
              <>
                <p className="text-xs text-muted-foreground mt-1">You ask the questions; the AI answers as the candidate you pick, then tells you why the answer is strong or weak.</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {REV_LEVELS.map((r) => <Pill key={r.id} active={revLevel === r.id} onClick={() => setRevLevel(r.id)} tid={`${testidPrefix}-iv-revlevel-${r.id}`}>{r.label}</Pill>)}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {modes.map((m) => (
            <button key={m.id} onClick={() => start(m)} disabled={busy} data-testid={`${testidPrefix}-iv-mode-${m.id}`}
              className="text-left rounded-xl border border-border bg-card p-5 hover:border-[color:var(--acc)] transition-colors">
              <MessagesSquare className="h-6 w-6" style={{ color: accent }} />
              <div className="font-semibold mt-3">{m.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.desc}</div>
              <div className="mt-3 text-xs">{m.best >= 50 ? <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Cleared ({m.best})</span> : m.best > 0 ? <span className="text-amber-500">Best: {m.best}/100</span> : <span className="text-muted-foreground">Not attempted</span>}</div>
            </button>
          ))}
        </div>
        {busy && <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Connecting your interviewer…</div>}
      </div>
    );
  }

  // -------- Report
  if (phase === "report" && report) {
    const r = report.report;
    const color = report.passed ? "#10b981" : "#f59e0b";
    return (
      <div className="max-w-2xl mx-auto px-6 py-8" data-testid={`${testidPrefix}-iv-report`}>
        <div className="rounded-2xl border-2 p-6" style={{ borderColor: color }}>
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold" style={{ color }}>{r.overall}<span className="text-base text-muted-foreground">/100</span></div>
            <div><div className="font-semibold text-lg">{report.passed ? "Passed ✓" : "Keep practicing"}</div>
              <div className="text-xs text-muted-foreground">Level demonstrated: {r.seniority} · {label} · {fmt(seconds)}</div></div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-5 text-center text-xs">
            {SUB_LABELS.filter(([k]) => typeof r[k] === "number").slice(0, 3).map(([k, lbl]) => (
              <div key={k} className="rounded-lg bg-muted py-2"><div className="font-semibold text-sm">{r[k]}</div><div className="text-muted-foreground">{lbl}</div></div>
            ))}
          </div>
          <Block title="Strengths" items={r.strengths} tone="emerald" />
          <Block title="Gaps to close" items={r.gaps} tone="amber" />
          <Block title="Red flags" items={r.red_flags} tone="red" />
          {r.better_answer && <div className="mt-3 text-sm"><div className="font-semibold">Model answer</div><p className="text-muted-foreground mt-0.5">{r.better_answer}</p></div>}
          <Block title="Expect these follow-ups" items={r.follow_ups} tone="slate" />
        </div>
        <button onClick={backToModes} className="mt-4 w-full py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-accent" data-testid={`${testidPrefix}-iv-done`}>Back to interview modes</button>
      </div>
    );
  }

  // -------- Live call
  const statusLabel = speaking ? "SPEAKING…" : listening ? "YOUR TURN — LISTENING…" : busy ? "THINKING…" : handsFree ? "YOUR TURN" : "TAP MIC TO ANSWER";
  const cmdBtns = [
    { action: "repeat", label: "Repeat", icon: RefreshCw },
    { action: "explain", label: "Explain", icon: HelpCircle },
    { action: "hint", label: "Hint", icon: Lightbulb },
    { action: "answer", label: "Answer for me", icon: GraduationCap },
    { action: "retry", label: "Try again", icon: RotateCcw },
    { action: "skip", label: "Skip", icon: SkipForward },
  ];
  return (
    <div className="h-full flex flex-col lg:flex-row gap-3 p-3" style={{ ["--acc"]: accent }} data-testid={`${testidPrefix}-iv-call`}>
      <div className="relative flex-1 rounded-2xl bg-[#0a0a0c] overflow-hidden flex flex-col min-h-[360px]">
        <div className="flex items-start justify-between p-4">
          <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-1.5">
            <div className="text-white text-sm font-semibold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> {label}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">{PERSONA_OPTS.find((p) => p.id === persona)?.label} · {DIFF_OPTS.find((d) => d.id === difficulty)?.label}</div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span data-testid={`${testidPrefix}-iv-modebadge`} className="text-[10px] font-bold tracking-widest rounded-full px-2.5 py-1"
              style={reversalMode ? { background: "#0ea5a3", color: "#04201f" } : coachMode ? { background: "#f59e0b", color: "#1a1205" } : { background: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}>
              {reversalMode ? "ROLE REVERSAL" : coachMode ? "COACH MODE" : "INTERVIEWER MODE"}
            </span>
            {!reversalMode && <span className="text-xs text-white bg-white/10 rounded-full px-2.5 py-1" data-testid={`${testidPrefix}-iv-counter`}>Answered {answered}/{target}</span>}
            <span className="text-xs text-white/80 bg-white/10 rounded-full px-2.5 py-1 font-mono">{fmt(seconds)}</span>
          </div>
        </div>

        <div className="flex-1 grid place-items-center">
          <div className="flex flex-col items-center">
            <div className={`relative rounded-full p-1 transition-all ${speaking ? "animate-pulse" : ""}`}
              style={{ boxShadow: speaking ? `0 0 0 6px ${accent}55, 0 0 40px ${accent}` : listening ? "0 0 0 6px #ef444455" : "0 0 0 3px rgba(255,255,255,0.15)" }}>
              <img src={avatar} alt="Interviewer" className="h-40 w-40 rounded-full object-cover" style={{ border: `3px solid ${coachMode ? "#f59e0b" : accent}` }} />
            </div>
            <div className="mt-6 text-sm font-semibold tracking-widest" style={{ color: speaking ? accent : listening ? "#ef4444" : "rgba(255,255,255,0.6)" }}>{statusLabel}</div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 p-5">
          <button onClick={() => setHandsFree((v) => { const nv = !v; if (!nv) { stopListen(); } return nv; })} title={handsFree ? "Hands-free is on — the mic opens automatically" : "Hands-free is off — click the mic to answer"}
            data-testid={`${testidPrefix}-iv-handsfree`}
            className={`h-11 rounded-full px-3 grid place-items-center text-xs font-semibold border ${handsFree ? "text-white border-transparent" : "bg-white/10 text-white border-white/10"}`}
            style={handsFree ? { background: accent } : {}}>
            {handsFree ? "HANDS-FREE" : "MANUAL"}
          </button>
          <button onClick={() => setMuted((v) => { const nv = !v; if (nv) { cancelSpeech(); } return nv; })} title={muted ? "Unmute interviewer" : "Mute interviewer"}
            className="h-11 w-11 rounded-full grid place-items-center bg-white/10 text-white hover:bg-white/20" data-testid={`${testidPrefix}-iv-mute`}>
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <button onClick={toggleMic} disabled={!micOk || busy} title={listening ? "Stop talking" : "Answer by voice"}
            className={`h-14 w-14 rounded-full grid place-items-center transition-colors ${listening ? "bg-red-500 text-white animate-pulse" : "bg-white/10 text-white hover:bg-white/20"} disabled:opacity-40`} data-testid={`${testidPrefix}-iv-mic`}>
            {listening ? <Square className="h-5 w-5" /> : <Mic className="h-6 w-6" />}
          </button>
          <button onClick={endCall} disabled={busy} className="h-11 rounded-full px-5 grid place-items-center bg-red-600 hover:bg-red-700 text-white font-medium flex-row inline-flex items-center gap-2" data-testid={`${testidPrefix}-iv-endcall`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <PhoneOff className="h-5 w-5" />} End Call
          </button>
        </div>
      </div>

      <div className="lg:w-[26rem] shrink-0 rounded-2xl border border-border bg-card flex flex-col min-h-[300px]">
        <div className="flex items-center gap-2 px-4 h-11 border-b border-border shrink-0">
          <MessagesSquare className="h-4 w-4" style={{ color: accent }} />
          <span className="text-sm font-semibold">Live transcript</span>
          <button onClick={backToModes} className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Modes</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
          {msgs.map((m, i) => {
            const isYou = m.role === "candidate";
            const isCoach = m.role === "coach";
            const isCand = m.role === "aicand";
            const who = isYou ? "You" : isCoach ? "Coach" : isCand ? "Candidate" : "Interviewer";
            const cmp = compare[i];
            const key = cmp?.question || "";
            const atts = key ? (attemptsRef.current[key] || []) : [];
            return (
              <div key={i}>
                <div className={`flex ${isYou ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`${testidPrefix}-iv-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${isYou ? "text-white" : isCoach ? "bg-amber-500/10 border border-amber-500/30" : "bg-muted"}`}
                    style={isYou ? { background: accent } : {}}>
                    <div className="text-[9px] uppercase tracking-wide opacity-60 mb-0.5" style={isCoach ? { color: "#d97706" } : {}}>{who}</div>
                    {m.text}
                  </div>
                </div>
                {isYou && !reversalMode && (
                  <div className="flex justify-end mt-1">
                    {!cmp && <button onClick={() => runCompare(i)} disabled={busy} data-testid={`${testidPrefix}-iv-compare-${i}`}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-[color:var(--acc)]"><Scale className="h-3 w-3" /> Compare my answer</button>}
                    {cmp?.loading && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Comparing…</span>}
                  </div>
                )}
                {cmp?.data && (
                  <div className="mt-1.5 ml-auto max-w-[92%] rounded-xl border border-border bg-background p-3 text-xs" data-testid={`${testidPrefix}-iv-comparepanel-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold" style={{ color: cmp.data.score >= 60 ? "#10b981" : cmp.data.score >= 45 ? "#f59e0b" : "#ef4444" }}>{cmp.data.score}</span>
                      <span className="text-muted-foreground">/100 · {cmp.data.verdict}</span>
                    </div>
                    {atts.length > 1 && <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-500"><TrendingUp className="h-3 w-3" /> Attempts: {atts.join("% → ")}%{atts[atts.length - 1] >= 85 ? " · Interview ready!" : ""}</div>}
                    {!!(cmp.data.missed || []).length && <div className="mt-2"><div className="font-semibold text-amber-500">What I missed</div><ul className="mt-0.5 space-y-0.5">{cmp.data.missed.map((x, j) => <li key={j} className="text-muted-foreground flex gap-1"><span className="text-amber-500">•</span>{x}</li>)}</ul></div>}
                    {!!(cmp.data.improve || []).length && <div className="mt-2"><div className="font-semibold">How to improve</div><ul className="mt-0.5 space-y-0.5">{cmp.data.improve.map((x, j) => <li key={j} className="text-muted-foreground flex gap-1"><span style={{ color: accent }}>•</span>{x}</li>)}</ul></div>}
                    {cmp.data.recommended && <div className="mt-2"><div className="font-semibold">Recommended answer</div><p className="text-muted-foreground mt-0.5">{cmp.data.recommended}</p></div>}
                    <button onClick={() => retryQuestion(cmp.question)} data-testid={`${testidPrefix}-iv-compareretry-${i}`}
                      className="mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-white text-[11px]" style={{ background: accent }}><RotateCcw className="h-3 w-3" /> Try this question again</button>
                  </div>
                )}
              </div>
            );
          })}
          {busy && <div className="flex justify-start"><div className="bg-muted rounded-2xl px-3 py-2"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>

        <div className="px-3 pt-2 flex flex-wrap gap-1.5 border-t border-border">
          {!reversalMode && cmdBtns.map((c) => (
            <button key={c.action} onClick={() => runCommand(c.action)} disabled={busy}
              data-testid={`${testidPrefix}-iv-cmd-${c.action}`}
              className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2.5 py-1 hover:border-[color:var(--acc)] hover:text-[color:var(--acc)] disabled:opacity-40 transition-colors">
              <c.icon className="h-3 w-3" /> {c.label}
            </button>
          ))}
          {reversalMode && <div className="text-[11px] text-[color:var(--acc)]" data-testid={`${testidPrefix}-iv-reversal-hint`}>You're the interviewer — ask the candidate a question.</div>}
        </div>

        <div className="p-3">
          {closing && <div className="mb-2 text-xs text-center text-[color:var(--acc)]" data-testid={`${testidPrefix}-iv-closing`}>Closing round — ask the interviewer anything, or press <strong>End Call</strong> for your feedback.</div>}
          {finished && !closing && <div className="mb-2 text-xs text-center text-muted-foreground">All done — press <strong>End Call</strong> for your feedback.</div>}
          <div className="flex items-end gap-2">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2}
              placeholder={reversalMode ? "Ask the candidate an interview question…" : "Speak (mic) or type — answer, or ask the interviewer a question…"} data-testid={`${testidPrefix}-iv-input`} className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none" />
            <button onClick={send} disabled={busy} data-testid={`${testidPrefix}-iv-send`} className="h-10 w-10 shrink-0 rounded-lg text-white grid place-items-center" style={{ background: accent }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
